/*
 * Feature route registry.
 *
 * featureRoutes is the single array the shell spreads into the protected
 * <Outlet> (see app/App.tsx). Each module (Chat / CRM / Admin / Expense) owns a
 * route list under features/<name>/routes.tsx and APPENDS it here — modules never
 * edit App.tsx. This keeps the shell decoupled from the modules (the keystone of
 * the consolidation plan's module_structure).
 *
 * Path convention: feature paths are RELATIVE to the authenticated root ('/').
 * The SHELL owns '/' (the landing redirect lives in App.tsx) — features must NOT
 * declare a path='' index route.
 *
 * NOTE: the Expense routes + the CRM/Settings/Support placeholders still live
 * inline in App.tsx for now (they predate the registry); they will migrate into
 * this array as those modules are lifted.
 */
import type { ReactElement } from 'react';
import { adminRoutes } from './admin';
import { chatFullBleedPaths, chatRoutes } from './chat';
import {
  commissionsFullBleedPaths,
  commissionsRoutes,
  commissionsStandaloneRoutes,
} from './commissions';
import { crmRoutes } from './crm';
import { csmStaffingRoutes } from './csm-staffing';
import { hotelSupplierRoutes } from './hotel-supplier';

/**
 * A feature's contribution to the authenticated route tree: an array of
 * react-router <Route> elements. Each MUST carry a stable `key`.
 */
export type FeatureRouteList = ReactElement[];

/** Ordered registry of every lifted feature's routes. */
export const featureRoutes: FeatureRouteList = [
  ...chatRoutes,
  ...adminRoutes,
  ...crmRoutes,
  ...hotelSupplierRoutes,
  ...csmStaffingRoutes,
  ...commissionsRoutes,
];

/**
 * Routes mounted inside the auth gate (ProtectedRoute) but OUTSIDE the shell:
 * no rail, no top bar. For secondary windows a module opens itself, like the
 * commission workbench's pop-out contract viewer. App.tsx spreads this list as
 * a sibling of the shell route; modules still never edit App.tsx.
 */
export const featureStandaloneRoutes: FeatureRouteList = [
  ...commissionsStandaloneRoutes,
];

/**
 * Absolute paths (each matching its whole subtree) whose screens fill the
 * shell's content area edge to edge instead of sitting in the padded gutter.
 * Only app-style surfaces that own their own columns and scrolling belong here.
 */
export type FullBleedPathList = readonly string[];

/** Every feature's full-bleed paths. ShellLayout reads this. */
export const fullBleedPaths: FullBleedPathList = [
  ...chatFullBleedPaths,
  ...commissionsFullBleedPaths,
];
